import React, {
  useState, useRef, useCallback, useEffect,
} from 'react';
import {
  withGoogleMap, withScriptjs, GoogleMap, Marker,
} from 'react-google-maps';
import PropTypes from 'prop-types';

import LocationBlueIcon from 'assets/images/LocationBlue.svg';

import styles from './Map.module.scss';

function Map({
  newDefaultPosition, mapRef, handleMapClick, onPlaceSelected,
}) {
  const [searchValue, setSearchValue] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef(null);
  const searchBoxRef = useRef(null);

  // Khởi tạo google.maps.places.SearchBox trực tiếp trên input
  useEffect(() => {
    if (!inputRef.current || !window.google) return undefined;

    const searchBox = new window.google.maps.places.SearchBox(inputRef.current);
    searchBoxRef.current = searchBox;

    const listener = searchBox.addListener('places_changed', () => {
      const places = searchBox.getPlaces();
      if (!places || places.length === 0) return;

      const place = places[0];
      if (!place.geometry || !place.geometry.location) return;

      const lat = place.geometry.location.lat();
      const lng = place.geometry.location.lng();

      setSearchValue(place.formatted_address || place.name || '');

      if (mapRef.current) {
        mapRef.current.panTo({ lat, lng });
        mapRef.current.setZoom(15);
      }

      if (onPlaceSelected) {
        onPlaceSelected({ lat, lng });
      }
    });

    return () => {
      window.google.maps.event.removeListener(listener);
    };
  }, [mapRef, onPlaceSelected]);

  const handleInputChange = useCallback((e) => {
    setSearchValue(e.target.value);
  }, []);

  const handleClear = useCallback(() => {
    setSearchValue('');
    if (inputRef.current) {
      inputRef.current.value = '';
    }
  }, []);

  return (
    <GoogleMap
      defaultZoom={15}
      defaultCenter={newDefaultPosition.lat
        ? newDefaultPosition : { lat: 16.056544, lng: 108.222921 }}
      onClick={handleMapClick}
      ref={mapRef}
      defaultOptions={{
        streetViewControl: false,
      }}
    >
      <div className={`${styles.SearchWrapper} ${isFocused ? styles.SearchWrapperFocused : ''}`}>
        <span className={styles.SearchIcon}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
        </span>
        <input
          ref={inputRef}
          type="text"
          value={searchValue}
          onChange={handleInputChange}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          placeholder="Tìm kiếm địa danh..."
          className={styles.SearchInput}
          aria-label="Tìm kiếm địa danh"
        />
        {searchValue && (
          <button
            type="button"
            className={styles.ClearButton}
            onClick={handleClear}
            title="Xóa tìm kiếm"
            aria-label="Xóa tìm kiếm"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        )}
      </div>

      <Marker
        icon={{
          url: LocationBlueIcon,
          scaledSize: new window.google.maps.Size(40, 40),
        }}
        position={{ lat: newDefaultPosition.lat, lng: newDefaultPosition.lng }}
      />
    </GoogleMap>
  );
}

Map.propTypes = {
  newDefaultPosition: PropTypes.instanceOf(Object).isRequired,
  mapRef: PropTypes.instanceOf(Object).isRequired,
  handleMapClick: PropTypes.func.isRequired,
  onPlaceSelected: PropTypes.func,
};

Map.defaultProps = {
  onPlaceSelected: null,
};

export default withScriptjs(withGoogleMap(Map));
